import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API, getLandingPath } from '@/App';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [microsoftEnabled, setMicrosoftEnabled] = useState(false);
  const { login, verifyLoginOtp, resendLoginOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // OTP step state — can be seeded from location.state when redirected back
  // from the Microsoft SSO callback for a first-login OTP challenge.
  const [otpStep, setOtpStep] = useState(() => location.state?.otpStep || null);
  const [otp, setOtp] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpResendCooldown, setOtpResendCooldown] = useState(() => (location.state?.otpStep ? 30 : 0));

  useEffect(() => {
    // Clear router state once consumed so a manual refresh of /login
    // doesn't keep replaying the OTP step.
    if (location.state?.otpStep) {
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (otpResendCooldown <= 0) return;
    const t = setTimeout(() => setOtpResendCooldown(s => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [otpResendCooldown]);

  // Check if Microsoft SSO is enabled on mount
  useEffect(() => {
    const checkMicrosoftConfig = async () => {
      try {
        const response = await axios.get(`${API}/auth/microsoft/config`);
        setMicrosoftEnabled(response.data.enabled === true);
      } catch (err) {
        // Retry once after delay (in case of rate limiting)
        try {
          await new Promise(resolve => setTimeout(resolve, 1500));
          const retry = await axios.get(`${API}/auth/microsoft/config`);
          setMicrosoftEnabled(retry.data.enabled === true);
        } catch {
          setMicrosoftEnabled(false);
        }
      }
    };
    checkMicrosoftConfig();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await login(email, password);
      if (data?.requires_otp) {
        setOtpStep({
          user_id: data.otp_user_id,
          email: data.otp_email,
          delivery_failed: data.otp_meta?.delivery_failed === true,
        });
        setOtpResendCooldown(30);
      } else {
        navigate(getLandingPath(data?.user), { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setOtpBusy(true);
    try {
      const data = await verifyLoginOtp(otpStep.user_id, otp.trim());
      navigate(getLandingPath(data?.user), { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code.');
      setOtp('');
    } finally {
      setOtpBusy(false);
    }
  };

  const handleOtpResend = async () => {
    if (otpResendCooldown > 0) return;
    setError('');
    try {
      const meta = await resendLoginOtp(otpStep.user_id);
      setOtpStep((s) => ({ ...s, delivery_failed: meta?.delivery_failed === true }));
      setOtpResendCooldown(30);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not resend code.');
    }
  };

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    setError('');
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMicrosoftLogin = async () => {
    setMicrosoftLoading(true);
    setError('');

    try {
      const response = await axios.get(`${API}/auth/microsoft/login`, {
        params: {
          redirect_uri: window.location.origin + '/auth/microsoft/callback'
        }
      });
      window.location.href = response.data.auth_url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Microsoft login is not available. Please use email login or Google.');
      setMicrosoftLoading(false);
    }
  };

  // Show password toggle
  const [showPwd, setShowPwd] = useState(false);

  // ── OTP STEP ────────────────────────────────────────────────────────
  if (otpStep) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fff7f5] dark:bg-slate-900 px-4 py-12 relative overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-[420px] h-44 rounded-3xl bg-red-100/60 dark:bg-red-900/30"></div>
          <div className="absolute top-12 right-[10%] w-[280px] h-[180px] rounded-3xl bg-red-100/50 dark:bg-red-900/25"></div>
          <div className="absolute bottom-12 right-[12%] w-[300px] h-[160px] rounded-3xl border-2 border-dashed border-red-200 dark:border-red-700/50"></div>
          <div className="absolute -bottom-10 left-[14%] w-[220px] h-[120px] rounded-3xl bg-red-100/50 dark:bg-red-900/30"></div>
          <div className="hidden dark:block absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(239,68,68,0.12),_transparent_60%),radial-gradient(circle_at_bottom_right,_rgba(239,68,68,0.10),_transparent_55%)]"></div>
        </div>

        <div className="relative w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 px-8 py-10 sm:px-10 sm:py-12">
          <div className="flex items-center justify-center mb-6">
            <img src="/logo-red-tight.png" alt="intalk.io" className="h-12 w-auto object-contain" />
          </div>

          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white text-center leading-tight">
            Verify it's you 🔐
          </h1>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 text-center">
            We sent a 6-digit code to <span className="font-medium text-slate-700 dark:text-slate-200">{otpStep.email}</span>.<br />
            Enter it below to finish signing in.
          </p>

          {otpStep.delivery_failed && (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
              <p className="text-xs text-amber-800 dark:text-amber-200">
                <strong>Heads up:</strong> we couldn't deliver the code by email. Please contact your administrator — they can retrieve it from the server logs.
              </p>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-md" role="alert">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          <form onSubmit={handleOtpSubmit} className="mt-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                Verification code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-3.5 py-3 bg-red-50/50 dark:bg-slate-700/40 border-2 border-red-300 dark:border-slate-600 rounded-lg text-center text-2xl font-mono tracking-[0.4em] text-slate-800 dark:text-slate-100 focus:outline-none focus:border-red-500 dark:focus:border-red-500 focus:bg-red-50 dark:focus:bg-slate-700 transition-colors"
                placeholder="• • • • • •"
                data-testid="otp-input"
              />
            </div>

            <button
              type="submit"
              disabled={otpBusy || otp.length !== 6}
              className="w-full px-4 py-3 bg-red-500 hover:bg-red-600 active:bg-red-700 text-white font-semibold text-sm rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
              data-testid="otp-verify-button"
            >
              {otpBusy ? 'Verifying…' : 'Verify & sign in'}
            </button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => { setOtpStep(null); setOtp(''); setError(''); }}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              >
                ← Use a different email
              </button>
              <button
                type="button"
                onClick={handleOtpResend}
                disabled={otpResendCooldown > 0}
                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium disabled:opacity-50"
              >
                {otpResendCooldown > 0 ? `Resend in ${otpResendCooldown}s` : 'Resend code'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fff7f5] dark:bg-slate-900 px-4 py-12 relative overflow-hidden">
      {/* Decorative red rectangles in the background */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-[420px] h-44 rounded-3xl bg-red-100/60 dark:bg-red-900/30"></div>
        <div className="absolute top-12 right-[10%] w-[280px] h-[180px] rounded-3xl bg-red-100/50 dark:bg-red-900/25"></div>
        <div className="absolute bottom-12 right-[12%] w-[300px] h-[160px] rounded-3xl border-2 border-dashed border-red-200 dark:border-red-700/50"></div>
        <div className="absolute -bottom-10 left-[14%] w-[220px] h-[120px] rounded-3xl bg-red-100/50 dark:bg-red-900/30"></div>
        {/* Soft red radial glow only in dark mode for a richer backdrop */}
        <div className="hidden dark:block absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(239,68,68,0.12),_transparent_60%),radial-gradient(circle_at_bottom_right,_rgba(239,68,68,0.10),_transparent_55%)]"></div>
      </div>

      {/* Card */}
      <div className="relative w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 px-8 py-10 sm:px-10 sm:py-12">
        {/* Logo */}
        <div className="flex items-center justify-center mb-6">
          <img src="/logo-red-tight.png" alt="intalk.io" className="h-12 w-auto object-contain" />
        </div>

        {/* Heading */}
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white text-center leading-tight">
          Welcome to <span className="font-bold">intalk.io</span>{' '}
          <span className="text-slate-700 dark:text-slate-300">SalesCRM</span>{' '}
          <span className="inline-block">👋</span>
        </h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 text-center">
          Please sign-in to your account and start the adventure.
        </p>

        {error && (
          <div className="mt-5 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-md" role="alert">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* SSO buttons */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            className="flex items-center justify-center gap-2 px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-600 hover:border-slate-400 dark:hover:border-slate-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="google-login-button"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-xs">{googleLoading ? 'Redirecting…' : 'Continue with Google'}</span>
          </button>

          <button
            type="button"
            onClick={handleMicrosoftLogin}
            disabled={microsoftLoading || !microsoftEnabled}
            className="flex items-center justify-center gap-2 px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-600 hover:border-slate-400 dark:hover:border-slate-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="microsoft-login-button"
            title={!microsoftEnabled ? 'Microsoft SSO not configured' : ''}
          >
            <svg className="w-4 h-4" viewBox="0 0 23 23">
              <path fill="#f35325" d="M0 0h11v11H0z"/>
              <path fill="#81bc06" d="M12 0h11v11H12z"/>
              <path fill="#05a6f0" d="M0 12h11v11H0z"/>
              <path fill="#ffba08" d="M12 12h11v11H12z"/>
            </svg>
            <span className="text-xs">{microsoftLoading ? 'Redirecting…' : 'Continue with Microsoft'}</span>
          </button>
        </div>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200 dark:border-slate-700"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="px-3 text-xs text-slate-400 bg-white dark:bg-slate-800">or sign in with email</span>
          </div>
        </div>

        {/* Email/Password form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Email or Username <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-red-50/50 dark:bg-slate-700/40 border-2 border-red-300 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:border-red-500 dark:focus:border-red-500 focus:bg-red-50 dark:focus:bg-slate-700 transition-colors"
              placeholder="you@company.com"
              data-testid="login-email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3.5 py-2.5 pr-10 bg-red-50/30 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:border-red-400 dark:focus:border-red-500 focus:bg-red-50/50 dark:focus:bg-slate-700 transition-colors"
                placeholder="••••••••••••"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPwd(s => !s)}
                tabIndex={-1}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                aria-label={showPwd ? 'Hide password' : 'Show password'}
              >
                {showPwd ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-red-500 focus:ring-red-400" />
              <span>Remember me</span>
            </label>
            <Link to="/forgot-password" className="text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
              Forgot Password ?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-3 bg-red-500 hover:bg-red-600 active:bg-red-700 text-white font-semibold text-sm rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
            data-testid="login-submit-button"
          >
            {loading ? 'Signing in…' : 'Login'}
          </button>
        </form>

        <p className="mt-6 text-sm text-slate-500 dark:text-slate-400 text-center">
          New on our platform?{' '}
          <Link to="/register" className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium" data-testid="login-signup-link">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
